import i18n from '@/i18n';
import { default as apiService } from '@/services/api.ts';
import { useAuthStore } from '@/stores/auth';
import type { InvoiceLineTaxType } from '@/types';
import { log } from '@/utils/log';
import { sortTaxes } from '@/utils/tax';
import type { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * localStorage key for the persisted reference-data cache. Exported so tests and
 * any external cleanup can wipe it deterministically.
 */
export const REFERENCE_DATA_PERSIST_KEY = 'app.populators';

/** A company's tax rates plus the moment they were fetched. */
interface CachedTaxRates {
    data: InvoiceLineTaxType[];
    timestamp: number;
    /** TAX_RATES_CACHE_VERSION at fetch time; an entry without one counts as outdated. */
    version?: number;
    /** Language of the request: the API names the codes in it, so an entry in another language is outdated too. */
    locale?: string;
}

/**
 * Bump when the shape of a cached tax changes, so an older entry is refetched at once
 * instead of served for the rest of the hour. Offline it is still served.
 */
const TAX_RATES_CACHE_VERSION = 2;

/** Fallback cache key when no active company is resolved (defensive only). */
const GLOBAL_KEY = '__global__';

/** How long a cached set of tax rates is considered fresh while online. */
const TAX_RATES_CACHE_DURATION = 1000 * 60 * 60; // 1 hour

/**
 * The language a response was requested in. At login the locale changes between the call
 * and the request, so the header that went out says what the entry holds, not the call.
 */
function requestedLocale(response: AxiosResponse, fallback: string): string {
    const sent: unknown = response.config?.headers?.['Accept-Language'];

    return typeof sent === 'string' ? sent : fallback;
}

/**
 * Holds form reference data (currently the invoice tax / BTW codes) that the
 * invoice, quote, recurring-invoice, catalog and project forms all need to even
 * render. Unlike list data, this is small, rarely changes, and is shared across
 * features — so it is cached once, persisted to localStorage, and served stale
 * while offline.
 *
 * The cache is keyed by company id: tax codes can differ per company, and a
 * company switch triggers a full page reload (see CompanySwitcher.vue), so a
 * single flat cache would risk serving the previous company's rates after the
 * reload rehydrates localStorage. Keying by company keeps that safe. An entry
 * also remembers its language, so a language switch refetches the names.
 *
 * This store is excluded from `$ResetPinia().all()` (see RESET_EXCLUDED_STORES in
 * src/pinia.ts) so the cache survives the login/logout reset — that reset would
 * otherwise wipe the persisted copy exactly when an offline boot needs it. It is
 * intentionally NOT cleared on signOut (unlike syncQueue): the data is
 * non-sensitive and company-keyed, and keeping it lets a returning user open the
 * create form offline.
 */
export const useReferenceDataStore = defineStore(
    'referenceData',
    () => {
        // Persisted: { [companyId]: { data, timestamp, version, locale } }
        const taxRatesByCompany = ref<Record<string, CachedTaxRates>>({});

        // In-flight fetch dedup per company. Closure-private (holds promises, so it
        // must never become reactive state, be snapshotted, or be persisted).
        const fetchPromises = new Map<string, Promise<InvoiceLineTaxType[]>>();

        function companyKey(): string {
            return useAuthStore().activeCompany?.id ?? GLOBAL_KEY;
        }

        /**
         * Returns the tax / BTW codes for the active company.
         *
         * Online: serves a fresh cache, otherwise fetches (deduped) and updates the
         * cache, falling back to a stale cache if the request fails.
         *
         * Offline: serves whatever is cached for this company — even if stale — and
         * never touches the network, so the form renders instantly instead of
         * hanging on a request that can only time out. An empty list is the graceful
         * last resort when nothing was ever cached.
         */
        async function getTaxRates(): Promise<InvoiceLineTaxType[]> {
            const key = companyKey();
            const entry = taxRatesByCompany.value[key];

            if (!navigator.onLine) {
                if (entry) {
                    log('Offline — serving cached tax rates for company', key);
                    return sortTaxes(entry.data);
                }
                log('Offline — no cached tax rates for company', key);
                return [];
            }

            const locale = i18n.global.locale.value;
            const isCurrent = entry?.version === TAX_RATES_CACHE_VERSION && entry.locale === locale;

            if (entry && isCurrent && Date.now() - entry.timestamp < TAX_RATES_CACHE_DURATION) {
                log('Using cached tax rates for company', key);
                return sortTaxes(entry.data);
            }

            const inflight = fetchPromises.get(key);
            if (inflight) return inflight;

            const promise = (async () => {
                try {
                    const response = await apiService.invoice.populators(key === GLOBAL_KEY ? undefined : key);
                    const taxes: InvoiceLineTaxType[] = response.data.data.taxes;

                    // Reassign (not mutate) so the persistence plugin reliably picks
                    // up the change.
                    taxRatesByCompany.value = {
                        ...taxRatesByCompany.value,
                        [key]: {
                            data: taxes,
                            timestamp: Date.now(),
                            version: TAX_RATES_CACHE_VERSION,
                            locale: requestedLocale(response, locale),
                        },
                    };

                    return sortTaxes(taxes);
                } catch (error) {
                    log('Failed to load tax rates:', error);
                    // A stale cache still beats an error for the user.
                    if (entry) {
                        log('Serving stale cached tax rates as fallback');
                        return sortTaxes(entry.data);
                    }
                    throw error;
                } finally {
                    fetchPromises.delete(key);
                }
            })();

            fetchPromises.set(key, promise);
            return promise;
        }

        /**
         * Proactively populate the cache for the active company while online, so the
         * reference data is already in localStorage before the user goes offline.
         * Fire-and-forget: never throws, no-op when offline or already fresh.
         */
        async function warm(): Promise<void> {
            if (!navigator.onLine) return;
            try {
                await getTaxRates();
            } catch (error) {
                log('Failed to warm reference data:', error);
            }
        }

        /** Wipe the whole cache. Not wired to signOut — see store docblock. */
        function clear(): void {
            taxRatesByCompany.value = {};
            fetchPromises.clear();
        }

        return {
            taxRatesByCompany,
            getTaxRates,
            warm,
            clear,
        };
    },
    {
        persist: {
            key: REFERENCE_DATA_PERSIST_KEY,
            pick: ['taxRatesByCompany'],
            storage: localStorage,
        } as any,
    },
);
